import { useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Divider, IconButton, Typography, Box, TextField, Stack, Button, FormHelperText, Grid } from '@mui/material'
import { Clear, CloudUpload } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequestWithdrawalByAdminMutation } from 'src/_api/mapitShipInvoice'
import { joiString, joiNumber, joiObject, runJoiValidate } from 'src/_utils'
import Joi from 'joi'
import MapitFileInput from 'src/services/shared/components/inputs/MapitFileInput'
import FilePreview from 'src/_modules/MapitShip/views/CodInvoices/components/FilePreview'
import { TextFieldNumber } from 'src/_modules/common'
import { omitBy } from 'lodash'

export default function TransferToMerchantDialog({
    invoice, // {metchant : {id: string, name: string}}
    open,
    onClose,
}) {
    const { t } = useTranslation()

    // Validation schema
    const SCHEMA = Joi.object({
        amount: joiNumber.number().min(1).label(t("Amount")).required(),
        merchant: joiString.string().label(t("Merchant")).required(),
        transferApprovalFile: joiString.string().label(t("Transfer confirmation file")).required()
    })

    const [requestWithdrawalByAdmin,
        {
            isLoading,
            isSuccess,
            error,
            reset
        }] = useRequestWithdrawalByAdminMutation()

    const fileInputRef = useRef(null)

    const [amount, setAmount] = useState('')
    const [transferApprovalFile, setTransferApprovalFile] = useState('')
    const [fileError, setFileError] = useState('')
    const [validationErrors, setValidationErrors] = useState(null)

    const handleFileChange = ({ target: { value } }) => {
        setTransferApprovalFile(value)
        setFileError('')
        setValidationErrors(null)
    }

    const handleFileError = (error) => {
        setFileError(error)
        setTransferApprovalFile('')
    }

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const requestWithdrawalHandler = async () => {
        try {
            setValidationErrors(null)

            // Prepare payload for validation
            let payload = {
                amount: parseFloat(amount),
                merchant: invoice?.merchant?.id,
                transferApprovalFile: transferApprovalFile
            }

            // Run validation
            const errors = runJoiValidate(SCHEMA, omitBy(payload, v => !v))

            if (!!errors) {
                setValidationErrors(errors)
                return
            }

            await requestWithdrawalByAdmin(payload).unwrap()

            setTimeout(() => {
                onCloseHandler()
            }, 3000);

        } catch (error) {
            console.log(error)
        }
    }

    const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('md'))

    const onCloseHandler = () => {
        onClose()
        reset()
        setAmount('')
        setTransferApprovalFile('')
        setFileError('')
        setValidationErrors(null)
    }

    useEffect(() => {
        if (!open) {
            reset()
        }
    }, [open])

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            onClose={(e) => e.preventDefault()}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
                {t("Transfer Money to Merchant")}
                <IconButton sx={{ ml: "auto" }} onClick={onCloseHandler} color="primary">
                    <Clear />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignContent="center" gap={1}>
                            <Typography  fontWeight="bold" color="textSecondary">
                                {t('Merchant')}
                            </Typography>
                            <Typography  fontWeight="bold">
                                {invoice?.merchant?.companyName || '-'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignContent="center" gap={1}>
                            <Typography  fontWeight="bold" color="textSecondary">
                                {t('Available Balance for Transfer')}
                            </Typography>
                            <Typography  fontWeight="bold">
                                {invoice?.dueAmount?.toFixed(2) || '-'}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextFieldNumber
                            label={t("Amount")}
                            variant="outlined"
                            size="small"
                            required={true}
                            decimal={true}
                            max={invoice?.dueAmount}
                            value={amount}
                            onChange={(e) => {
                                setAmount(e.target.value)
                                setValidationErrors(null)
                            }}
                            error={!!validationErrors?.amount}
                            helperText={validationErrors?.amount}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Stack>
                                <Button
                                    variant="outlined"
                                    startIcon={<CloudUpload />}
                                    onClick={triggerFileInput}
                                    disabled={isLoading}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('Transfer confirmation file')}
                                </Button>

                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    {t('Supported formats: JPG, JPEG, PNG, GIF, BMP, PDF (Max 5MB)')}
                                </Typography>
                                <FormHelperText error={!!validationErrors?.transferApprovalFile}>
                                    {validationErrors?.transferApprovalFile}
                                </FormHelperText>
                            </Stack>
                            {fileError && (
                                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                    {fileError}
                                </Typography>
                            )}
                        </Box>
                        <MapitFileInput
                            ref={fileInputRef}
                            name="transactionFile"
                            accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                            maxSize={5}
                            onChange={handleFileChange}
                            onError={handleFileError}
                        />
                    </Grid>
                </Grid>
                <FilePreview file={transferApprovalFile} />
            </DialogContent>
            <Divider />
            <DialogActions>
                {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                {!!error && <Typography color="error">{error}</Typography>}
                <Box mr="auto" />
                <Button
                    variant="contained"
                    onClick={onCloseHandler}
                    sx={{ textTransform: "none" }}
                    disabled={isLoading}
                >
                    {t("Close")}
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={requestWithdrawalHandler}
                    sx={{ textTransform: "none" }}
                    loading={isLoading}
                    disabled={isSuccess}
                >
                    {t("Confirm")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
